import { User, UserLoginOrRegistrationRequest, UserSocial } from '../../types';
import { GoogleOAuthConfig, GoogleCredentialResponse, GoogleButtonConfig, GooglePromptNotification, GoogleOAuth2Config, GoogleOAuth2Response } from '../../types';
import { sendUserToBackend } from './backendAuth';

// 환경변수에서 설정값 가져오기
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your_google_client_id_here';
const GOOGLE_REDIRECT_URI = process.env.REACT_APP_GOOGLE_REDIRECT_URI || 'http://localhost:3000/auth/google/callback';

// Google SDK 로드 함수
const loadGoogleSDK = async (): Promise<boolean> => {
  if (window.google) {
    return true;
  }

  try {
    const googleScript = document.createElement('script');
    googleScript.src = 'https://accounts.google.com/gsi/client';
    googleScript.async = true;
    googleScript.defer = true;
    
    await new Promise<void>((resolve, reject) => {
      googleScript.onload = () => resolve();
      googleScript.onerror = () => reject(new Error('Google SDK 로드 실패'));
      document.head.appendChild(googleScript);
    });

    // Google OAuth 초기화
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your_google_client_id_here';
    if (window.google && (window.google as any).accounts) {
      // FedCM 지원 여부 확인
      const isFedCMSupported = 'IdentityCredential' in window;
      
      // 기본 설정
      const initializeConfig: GoogleOAuthConfig = {
        client_id: googleClientId,
        callback: (response: any) => {
          console.log('Google OAuth 콜백:', response);
          if ((window as any).handleCredentialResponse) {
            (window as any).handleCredentialResponse(response);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin' as const
      };
      
      // FedCM 지원 시 추가 설정
      if (isFedCMSupported) {
        initializeConfig.use_fedcm_for_prompt = true;
        initializeConfig.prompt_parent_id = 'google-login-container';
        initializeConfig.state_cookie_domain = window.location.hostname;
        initializeConfig.ux_mode = 'popup';
        initializeConfig.itp_support = true;
      }
      
      try {
        (window.google as any).accounts.id.initialize(initializeConfig);
        console.log('Google OAuth 초기화 성공');
      } catch (error) {
        console.error('Google OAuth 초기화 실패:', error);
        // 기본 설정으로 재시도
        const fallbackConfig: GoogleOAuthConfig = {
          client_id: googleClientId,
          callback: initializeConfig.callback,
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signin' as const
        };
        (window.google as any).accounts.id.initialize(fallbackConfig);
        console.log('Google OAuth 기본 설정으로 초기화 성공');
      }
    }

    // console.log('Google SDK 로드 성공');
    return true;
  } catch (error) {
    console.error('Google SDK 로드 실패:', error);
    return false;
  }
};

// 개인화된 Google 로그인 버튼 생성 함수
const createPersonalizedGoogleButton = () => {
  // 기존 오버레이가 있다면 제거
  const existingOverlay = document.getElementById('google-login-overlay');
  if (existingOverlay) {
    existingOverlay.remove();
  }

  // 배경 오버레이 생성
  const overlay = document.createElement('div');
  overlay.id = 'google-login-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
  `;

  const loginContainer = document.createElement('div');
  loginContainer.id = 'google-login-button';
  loginContainer.style.cssText = `
    background: white;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.2);
    text-align: center;
    max-width: 400px;
    width: 90%;
  `;

  // 제목 추가
  const title = document.createElement('h3');
  title.textContent = 'Google 계정으로 로그인';
  title.style.cssText = `
    margin: 0 0 20px 0;
    color: #333;
    font-size: 18px;
    font-weight: 600;
  `;
  loginContainer.appendChild(title);

  // 설명 추가
  const description = document.createElement('p');
  description.textContent = 'Google 계정을 선택하여 로그인해주세요.';
  description.style.cssText = `
    margin: 0 0 20px 0;
    color: #666;
    font-size: 14px;
  `;
  loginContainer.appendChild(description);

  // Google 버튼 컨테이너
  const buttonContainer = document.createElement('div');
  buttonContainer.id = 'google-personalized-button-container';
  loginContainer.appendChild(buttonContainer);

  // 닫기 버튼 추가
  const closeButton = document.createElement('button');
  closeButton.textContent = '닫기';
  closeButton.style.cssText = `
    margin-top: 15px;
    padding: 8px 16px;
    border: 1px solid #ddd;
    background: #f8f9fa;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    color: #666;
  `;
  closeButton.onclick = () => {
    overlay.remove();
  };
  loginContainer.appendChild(closeButton);

  overlay.appendChild(loginContainer);
  document.body.appendChild(overlay);
  
  // FedCM 지원 여부에 따른 개인화된 버튼 렌더링
  const isFedCMSupported = 'IdentityCredential' in window;
  
  // 개인화된 버튼 설정 (FedCM 최신 버전 지원)
  const buttonConfig: GoogleButtonConfig = {
    type: 'standard', // 개인화된 버튼을 위해 'standard' 사용
    theme: 'outline',
    size: 'large',
    text: 'signin_with',
    shape: 'rectangular',
    logo_alignment: 'left',
    width: '300',
    context: 'signin'
  };
  
  // FedCM 지원 시 추가 설정
  if (isFedCMSupported) {
    buttonConfig.use_fedcm_for_prompt = true;
    buttonConfig.prompt_parent_id = 'google-personalized-button-container';
  }
  
  try {
    // 개인화된 버튼 렌더링
    if (window.google && window.google.accounts) {
      window.google.accounts.id.renderButton(buttonContainer, buttonConfig);
      console.log('Google 개인화된 버튼 렌더링 성공:', buttonConfig);
    }
  } catch (error) {
    console.error('Google 개인화된 버튼 렌더링 실패:', error);
    
    // 렌더링 실패 시 기본 버튼으로 대체
    const fallbackConfig: GoogleButtonConfig = {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: '300',
      context: 'signin'
    };
    
    try {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.renderButton(buttonContainer, fallbackConfig);
        console.log('Google 기본 버튼 렌더링 성공');
      }
    } catch (fallbackError) {
      console.error('Google 버튼 렌더링 완전 실패:', fallbackError);
      
      // 완전히 실패한 경우 수동 버튼 생성
      const manualButton = document.createElement('button');
      manualButton.textContent = 'Google로 로그인';
      manualButton.style.cssText = `
        width: 300px;
        height: 40px;
        border: 1px solid #dadce0;
        border-radius: 4px;
        background: white;
        color: #3c4043;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      `;
      
      manualButton.onclick = () => {
        // 수동 버튼 클릭 시 Google 로그인 재시도
        if (window.google && window.google.accounts) {
          window.google.accounts.id.prompt();
        }
      };
      
      buttonContainer.appendChild(manualButton);
    }
  }
  
  // 30초 후 자동 제거
  setTimeout(() => {
    const overlayToRemove = document.getElementById('google-login-overlay');
    if (overlayToRemove) {
      overlayToRemove.remove();
    }
  }, 30000);
};

// Google 로그인 (FedCM 최신 버전 지원)
export const googleFedCMLogin = async (navigate: any): Promise<UserSocial> => {
  return new Promise(async (resolve, reject) => {
    // SDK가 로드되지 않은 경우 로드
    if (!window.google || !window.google.accounts) {
      const sdkLoaded = await loadGoogleSDK();
      if (!sdkLoaded) {
        reject(new Error('Google SDK 로드에 실패했습니다.'));
        return;
      }
    }

    // Google 로그인 콜백 처리
    const handleCredentialResponse = async (response: GoogleCredentialResponse) => {
      try {
        console.log('Google OAuth 응답:', response);
        
        // JWT 토큰을 디코드하여 사용자 정보 추출
        const base64Url = response.credential.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        
        // UTF-8 인코딩을 올바르게 처리
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const jsonPayload = new TextDecoder('utf-8').decode(bytes);
        const payload = JSON.parse(jsonPayload);
        console.log('Google 사용자 정보:', payload);
        
        let socialUser: UserSocial = {
          id: `${payload.sub}`,
          name: payload.name,
          email: payload.email,
          picture: payload.picture,
          provider: 'google',
          accessToken: response.credential
        };

        const userLoginOrRegistrationRequest: UserLoginOrRegistrationRequest = {
          ...socialUser,
        };
        
        // 백엔드에 사용자 정보 전송
        const data = await sendUserToBackend(userLoginOrRegistrationRequest);

        if (data.success === false) {
          if ( data?.id !== undefined && data?.id !== null && data?.id !== '' ) {
            localStorage.setItem('kuid', data.id);

            if (!data?.agree_terms_of_service || !data?.agree_privacy_policy) {
              // 약관 동의가 필요한 경우 회원가입 플로우로 리다이렉트
              navigate('/join',{state:{social_provider:`google`, user:data}});
              return;
            }

            // if (!data.phone_number_verified) {
            //   // 전화번호 인증이 필요한 경우 전화번호 인증 플로우로 리다이렉트
            //   const url = `/phone-verification?kuid=${data.id}`;
            //   navigate(url);
            //   return;
            // }
          }

          // 로그인 실패
          console.log(data,'/[googleLogin googleLogin reject------------------------------>]');
          reject(new Error(data.message || '로그인(또는 사용자등록)에 실패했습니다.'));
          return;
        } else {
          // 로그인 성공
          resolve(data);
        }

      } catch (error) {
        console.error('Google 로그인 오류:', error);
        reject(error);
      }
    };

    // 콜백 함수를 window 객체에 추가
    (window as any).handleCredentialResponse = handleCredentialResponse;

    // FedCM 지원 여부 확인
    const isFedCMSupported = 'IdentityCredential' in window;
    // console.log('FedCM 지원 여부 (로그인):', isFedCMSupported);

         // Google One Tap 또는 개인화된 버튼 표시
     try {
       // One Tap 프롬프트 표시
       if (window.google && window.google.accounts && window.google.accounts.id) {
         window.google.accounts.id.prompt((notification: GooglePromptNotification) => {
        console.log('Google One Tap 알림:', notification);
        
        if (notification.isNotDisplayed()) {
          const reason = notification.getNotDisplayedReason();
          console.log('Google One Tap 표시되지 않음:', reason);
          
          // One Tap이 표시되지 않는 경우 개인화된 버튼 생성
          createPersonalizedGoogleButton();
          return;
        }
        
        if (notification.isSkippedMoment()) {
          const reason = notification.getSkippedReason();
          console.log('Google One Tap 건너뜀:', reason);
          
          // One Tap이 건너뛰어진 경우 개인화된 버튼 생성
          createPersonalizedGoogleButton();
          return;
        }
        
        if (notification.isDismissedMoment()) {
          const reason = notification.getDismissedReason();
          console.log('Google One Tap 해제됨:', reason);
          
          if (reason === 'credential_returned') {
            return; // 성공적으로 처리됨
          } else {
            // 사용자가 취소한 경우 개인화된 버튼 생성
            createPersonalizedGoogleButton();
            return;
          }
        }
      });
    } else {
      console.log('Google One Tap을 사용할 수 없습니다. 개인화된 버튼으로 대체합니다.');
      createPersonalizedGoogleButton();
    }
    } catch (error) {
      console.log('Google One Tap 실패, 개인화된 버튼으로 대체:', error);
      createPersonalizedGoogleButton();
    }
  });
};

// Google 로그인 (OAuth2 지원)
export const googleLogin = async (navigate: any): Promise<UserSocial> => {
  return new Promise(async (resolve, reject) => {
    // SDK가 로드되지 않은 경우 로드
    if (!window.google || !window.google.accounts) {
      const sdkLoaded = await loadGoogleSDK();
      if (!sdkLoaded) {
        reject(new Error('Google SDK 로드에 실패했습니다.'));
        return;
      }
    }

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your_google_client_id_here';
    
    if (!clientId || clientId === 'your_google_client_id_here') {
      reject(new Error('Google Client ID가 설정되지 않았습니다.'));
      return;
    }

    try {
      const config: GoogleOAuth2Config = {
        client_id: clientId,
        scope: 'openid https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email',
        ux_mode: 'popup',
        callback: async (response: GoogleOAuth2Response) => {
          try {
            // console.log('Google OAuth2 인증 코드 응답:', response.code);
            
            if (response.error) {
              console.error('Google OAuth2 오류:', response.error, response.error_description);
              reject(new Error(`Google OAuth2 오류: ${response.error} - ${response.error_description}`));
              return;
            }

            // 인증 코드를 사용하여 액세스 토큰과 사용자 정보를 가져옴
            const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: new URLSearchParams({
                code: response.code,
                client_id: clientId,
                client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET || '',
                redirect_uri: window.location.origin,
                grant_type: 'authorization_code',
              }),
            });

            if (!tokenResponse.ok) {
              const errorData = await tokenResponse.text();
              console.error('토큰 교환 실패:', errorData);
              reject(new Error('액세스 토큰을 가져오는데 실패했습니다.'));
              return;
            }

            const tokenData = await tokenResponse.json();
            const accessToken = tokenData.access_token;

            // 액세스 토큰을 사용하여 사용자 정보 가져오기
            const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
              },
            });

            if (!userInfoResponse.ok) {
              reject(new Error('사용자 정보를 가져오는데 실패했습니다.'));
              return;
            }

            const userInfo = await userInfoResponse.json();
            
            // UserSocial 객체 생성
            const socialUser: UserSocial = {
              id: userInfo.id,
              name: userInfo.name || 'Google 사용자',
              email: userInfo.email || '',
              picture: userInfo.picture || '',
              provider: 'google',
              accessToken: accessToken
            };
            const userLoginOrRegistrationRequest: UserLoginOrRegistrationRequest = {
              ...socialUser,
            };

            // 백엔드에 사용자 정보 전송
            const data = await sendUserToBackend(userLoginOrRegistrationRequest);
            console.log(data,'/[googleLogin sendUserToBackend data------------------------------>]');

            if (data.success === false) {
              if ( data?.id !== undefined && data?.id !== null && data?.id !== '' ) {
                localStorage.setItem('kuid', data.id);
    
                if (!data?.agree_terms_of_service || !data?.agree_privacy_policy) {
                  // 약관 동의가 필요한 경우 회원가입 플로우로 리다이렉트
                  navigate('/join',{state:{social_provider:`google`, user:data}});
                  return;
                }
    
                if (!data.phone_number_verified) {
                  // 전화번호 인증이 필요한 경우 전화번호 인증 플로우로 리다이렉트
                  const url = `/phone-verification?kuid=${data.id}`;
                  navigate(url);
                  return;
                }
              }
    
              // 로그인 실패
              console.log(data,'/[googleLogin googleLogin reject------------------------------>]');
              reject(new Error(data.message || '로그인(또는 사용자등록)에 실패했습니다.'));
              return;
            } else {
              // 로그인 성공
              resolve(data);
            }
          } catch (error) {
            console.error('Google 로그인 처리 중 오류:', error);
            reject(error);
          }
        },
      };

      // OAuth2 클라이언트 초기화
      if (!window.google || !window.google.accounts || !window.google.accounts.oauth2) {
        reject(new Error('Google OAuth2 SDK가 초기화되지 않았습니다.'));
        return;
      }
      
      const client = window.google.accounts.oauth2.initCodeClient(config);
      
      // 인증 코드 요청
      client.requestCode();
      
    } catch (error) {
      console.error('Google OAuth2 클라이언트 초기화 실패:', error);
      reject(new Error('Google OAuth2 클라이언트 초기화에 실패했습니다.'));
    }
  });
};
